import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, paragraphMedium, paragraphSmall, tagLarge } from '@helpers/typography';
import svgTick from '@img/icons/tick-black.svg';
import styled, { css } from 'styled-components';
const Container = styled.section `
  display: block;
`;
const Form = styled.form `
  display: block;
`;
const Fieldset = styled.fieldset `
  display: block;

  & + & {
    margin: 24px 0 0;
  }
`;
const Legend = styled.legend `
  ${tagLarge};

  display: block;
  margin: 0 0 ${fluid(24, 32)};
  text-transform: uppercase;
`;
const Field = styled.div `
  ${paragraphMedium};

  display: flex;
  flex-direction: column;
  gap: 8px 4px;
  margin: 0 0 ${fluid(20, 40)};
  position: relative;

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const Label = styled.label `
  label& {
    ${transition('transform')};

    position: absolute;
    left: 4px;
    top: 0;
    transform: translateY(${fluid(6, 10)});
    background-color: ${brand.beige};
    padding: 2px 12px;
    transform-origin: top left;

    ${Field}:focus-within &,
    ${Field}:has(input:not(:placeholder-shown), textarea:not(:placeholder-shown)) & {
      transform: translateY(calc(-50% + 2px)) scale(0.8);
    }
  }

  &[data-sr-only] {
    ${srOnly};
  }
`;
const ControlLabel = styled.label `
  font-weight: 400;

  > input {
    ${srOnly};
  }
`;
const CheckboxLabel = styled.span `
  position: relative;
  padding-left: ${fluid(38, 42)};

  &::after,
  &::before {
    content: '';
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    height: ${fluid(20, 25)};
    width: ${fluid(20, 25)};
  }

  &::after {
    ${transition('opacity', 0.25)};

    opacity: 0;
    background: transparent no-repeat center center;
    background-size: 14px 14px;
    background-image: url(${svgTick});
  }

  &::before {
    border: 1px solid ${brand.black};
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  .invalid &::before {
    border-color: ${brand.validation.invalid};
  }
`;
const RadioLabel = styled.span `
  position: relative;
  padding-left: ${fluid(32, 36)};

  &::after,
  &::before {
    content: '';
    position: absolute;
    display: block;
    border-radius: 50%;
    top: 50%;
    transform: translateY(-50%);
  }

  &::after {
    ${transition('opacity', 0.25)};

    opacity: 0;
    height: ${fluid(12, 16)};
    width: ${fluid(12, 16)};
    background-color: ${brand.green};
    left: 4px;
  }

  &::before {
    height: ${fluid(18, 22)};
    width: ${fluid(18, 22)};
    border: 1px solid ${brand.black};
    left: 0;
  }

  input:checked + & {
    &::after {
      opacity: 1;
    }
  }

  .invalid &::before {
    border-color: ${brand.validation.invalid};
  }
`;
const sharedInputStyles = css `
  ${transition('border-color, background-color')}

  display: block;
  background: ${brand.beige};
  border: 1px solid ${brand.black};
  color: ${brand.black};
  height: ${fluid(40, 55)};
  padding: 18px 16px;
  width: 100%;
  font-weight: 400;
  font-family: inherit;
  font-size: inherit;

  .invalid & {
    border-color: ${brand.validation.invalid};
  }
`;
const Input = styled.input `
  ${sharedInputStyles};
`;
const SelectWrapper = styled.div `
  ${paragraphMedium};

  width: 100%;
  position: relative;
  margin: 0;
`;
const Arrow = styled.div `
  display: grid;
  place-items: center;
  height: ${fluid(40, 55)};
  width: ${fluid(40, 55)};
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: none;

  > svg {
    ${transition('transform')};

    display: block;
    height: ${fluid(14, 18)};
    color: currentColor;
    transform: rotate(90deg);
  }

  .invalid & {
    border-color: ${brand.validation.invalid};
    color: ${brand.validation.invalid};
  }
`;
const Select = styled.select `
  ${sharedInputStyles};

  appearance: none;
  padding-right: 52px;
  padding-top: 0;
  padding-bottom: 0;

  option {
    background-color: var(--bgColor);
    color: ${brand.black};
  }

  &:focus ~ ${Arrow} > svg {
    transform: rotate(270deg);
  }
`;
const Textarea = styled.textarea `
  ${sharedInputStyles};

  height: auto;
  min-height: 163px;
`;
const Error = styled.span `
  display: block;
  font-size: ${fluid(14, 16)};
  line-height: 22px;
  font-weight: 400;
  margin: -2px 0 0;
  color: ${brand.validation.invalid};
`;
const Submit = styled.div `
  display: flex;
  align-items: center;
  flex-direction: row;
  margin: 24px 0 0;
`;
const ConfirmationMessage = styled.div `
  > svg {
    display: block;
    width: ${fluid(42, 64)};
    margin-bottom: ${fluid(24, 32)};
  }

  > span {
    ${contentStyles};
  }
`;
const ErrorList = styled.ul `
  display: block;
  margin: 0 0 ${fluid(32, 48)};
  color: ${brand.validation.invalid};
`;
const ErrorListItem = styled.li `
  ${paragraphMedium};

  margin: 0 0 ${fluid(8, 12)};
`;
const FieldTitle = styled.h2 `
  display: block;
  width: 100%;
  font-size: 120%;
`;
const FieldDescription = styled.p `
  font-weight: 400;

  display: block;
  width: 100%;
  margin: 0 0 12px;
`;
const List = styled.div `
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${fluid(8, 12)};
`;
const RecaptchaWrapper = styled.div `
  width: 100%;
`;
// File Upload Styles
const FileUploadLabelContainer = styled.div `
  display: flex;
  gap: ${fluid(8, 12)};
  align-items: center;
  width: 100%;
  overflow: hidden;

  > label {
    text-decoration: none;
    margin: 0;
    white-space: nowrap;
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
    }

    > svg {
      display: block;
      height: ${fluid(18, 20)};
      width: ${fluid(18, 20)};
      margin-right: ${fluid(12, 14)};
    }
  }
`;
const FileNameContent = styled.span `
  ${paragraphSmall};

  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
  margin: 0;

  @media ${from(Device.TabletLarge)} {
    max-width: 250px;
  }
`;
export default {
    Arrow,
    CheckboxLabel,
    ConfirmationMessage,
    Container,
    ControlLabel,
    Error,
    ErrorList,
    ErrorListItem,
    Field,
    FieldDescription,
    Fieldset,
    FieldTitle,
    Form,
    Input,
    Label,
    Legend,
    List,
    RadioLabel,
    RecaptchaWrapper,
    Select,
    SelectWrapper,
    Submit,
    Textarea,
    FileUploadLabelContainer,
    FileNameContent,
};
